import { gql } from 'graphql-tag';

// =============================================================================
// Program Access Policy — GraphQL operations against the Nexus backend
// =============================================================================
// The adaptive sign-in ladder. A Program carries
//   accessPolicy: { floor: 'off'|'verified'|'on', allowUpgrades: boolean }
// and the public-program auth dialog renders its primary/secondary actions per
// that floor (anonymous "Join now" → verified express → full account).
//
// The `enroll` mutation is the anonymous "Join now" rung: it creates a Student
// instance for a not-yet-authenticated participant, capturing acquisition
// signals (referrer, UTM, user-agent, language, timezone) for attribution.
//
// Modes:
//   ANONYMOUS — one-tap join, no identity (floor:'off')
//   VERIFIED  — name + email/phone + OTP (floor:'verified', the express rung)
//   ACCOUNT   — full account (floor:'on')
//
// NOTE: VERIFIED / ACCOUNT enrolment continues to flow through the existing
// auth orchestrator (express + full). Only ANONYMOUS uses this mutation
// directly from the client. See the access-policy spec.
// =============================================================================

export const ENROLL_MUTATION = gql`
  mutation Enroll($input: EnrollInput!) {
    enroll(input: $input) {
      studentId
      sharableUrl
      redirectUrl
    }
  }
`;

// ------- Client-side input shapes (mirror the backend EnrollInput) -------

export type EnrollMode = 'ANONYMOUS' | 'VERIFIED' | 'ACCOUNT';

export interface EnrollMetadata {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  userAgent?: string;
  language?: string;
  timezone?: string;
  /**
   * Anonymous visitor UUID (localStorage `pcv-visitor-id`). Threaded into enroll
   * so the resulting Student is attributed to the share-link visit that produced
   * it — the view→join conversion signal. See useVisitorId.
   */
  visitorId?: string;
}

export interface EnrollInput {
  programId: string;
  mode: EnrollMode;
  metadata?: EnrollMetadata;
}

export interface EnrollResult {
  studentId: string;
  sharableUrl: string | null;
  redirectUrl: string | null;
}
